import type { TabularSheetCellValue } from '../../shared/tabular-sheet-payload.model';

export interface DefraSubmissionField {
    readonly name: string;
    readonly value: TabularSheetCellValue;
}

function isPrimitive(value: unknown): value is string | number | boolean {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function joinKey(prefix: string, key: string): string {
    return prefix === '' ? key : `${prefix}.${key}`;
}

function primitiveArrayToCell(values: unknown[]): string {
    return values.map((value) => (value == null ? '' : String(value))).join(', ');
}

function collect(value: unknown, prefix: string, out: DefraSubmissionField[]): void {
    if (value === null || value === undefined) {
        if (prefix !== '') {
            out.push({ name: prefix, value: '' });
        }
        return;
    }

    if (isPrimitive(value)) {
        if (prefix !== '') {
            out.push({ name: prefix, value });
        }
        return;
    }

    if (Array.isArray(value)) {
        if (value.every((item) => item === null || item === undefined || isPrimitive(item))) {
            if (prefix !== '') {
                out.push({ name: prefix, value: primitiveArrayToCell(value) });
            }
            return;
        }
        value.forEach((item, index) => {
            collect(item, joinKey(prefix, String(index)), out);
        });
        return;
    }

    if (typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>);
        if (entries.length === 0) {
            if (prefix !== '') {
                out.push({ name: prefix, value: '' });
            }
            return;
        }
        for (const [key, nested] of entries) {
            collect(nested, joinKey(prefix, key), out);
        }
    }
}

/**
 * Expands the JSON Trace submitted to DEFRA into one name/value pair per leaf field.
 * Keys keep the submitted casing and use dotted paths (`carrier.registrationNumber`, `wasteItems.0.physicalForm`).
 */
export function flattenDefraSubmissionFields(value: unknown): DefraSubmissionField[] {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            return [];
        }
        try {
            return flattenDefraSubmissionFields(JSON.parse(trimmed));
        } catch {
            return [{ name: '', value: trimmed }];
        }
    }

    const fields: DefraSubmissionField[] = [];
    collect(value, '', fields);
    return fields;
}
